import { Link, useLocation } from "@remix-run/react";
import { useEffect } from "react";

import Logo from "~/components/logo/Logo";
import { CURRENT_OFFER } from "~/config/constants";
import { useBooking } from "~/context/BookingContext";
import { useLanguage } from "~/context/LanguageContext";
import {
  switchLanguagePath,
  useLocalizedPath,
} from "~/helpers/navigationHelper";
import { useDialog } from "~/hooks/useDialog";
import common from "~/i18n/common";
import { useT } from "~/i18n/useT";

import { LANGUAGES } from "./languages";

type Props = { isOpen: boolean; onClose: () => void };

export default function MobileMenu({ isOpen, onClose }: Props) {
  const t = useT(common);
  const { lang } = useLanguage();
  const { pathname } = useLocation();
  const localize = useLocalizedPath();
  const booking = useBooking();
  const ref = useDialog(isOpen, onClose);

  // A route change while the menu is open should dismiss it.
  useEffect(() => {
    if (isOpen) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  if (!isOpen) return null;

  const links = [
    { to: "/courses", label: t.nav.courses },
    { to: "/pricing", label: t.nav.pricing, offer: CURRENT_OFFER },
    { to: "/about", label: t.nav.about },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <div
      className="ss-mobile-menu"
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={t.nav.openMenu}
      tabIndex={-1}
    >
      <div className="ss-mobile-menu__head">
        <Logo variant="dark" />
        <button
          type="button"
          className="ss-mobile-menu__close"
          aria-label={t.nav.closeMenu}
          onClick={onClose}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>

      <nav className="ss-mobile-menu__links">
        {links.map((link) => (
          <Link
            key={link.to}
            to={localize(link.to)}
            className="ss-mobile-menu__link"
            onClick={onClose}
          >
            {link.label}
            {/* dir="ltr" keeps "-30%" from reordering to "30%-" in Arabic. */}
            {link.offer ? (
              <span className="ss-mobile-menu__offer" dir="ltr">
                -{link.offer}%
              </span>
            ) : null}
          </Link>
        ))}
      </nav>

      <div className="ss-mobile-menu__foot">
        <h2 className="ss-mobile-menu__label">{t.nav.language}</h2>
        <ul className="ss-mobile-menu__langs">
          {LANGUAGES.map((option) => (
            <li key={option.code}>
              <Link
                to={switchLanguagePath(pathname, option.code)}
                hrefLang={option.code}
                lang={option.code}
                className={`ss-mobile-menu__chip${
                  option.code === lang ? " is-active" : ""
                }`}
                onClick={onClose}
              >
                <span aria-hidden="true">{option.flag}</span>
                {option.label}
              </Link>
            </li>
          ))}
        </ul>

        <button
          type="button"
          className="ss-btn ss-btn--primary ss-mobile-menu__cta"
          onClick={() => {
            onClose();
            booking.open();
          }}
        >
          {t.nav.bookCall}
        </button>
      </div>
    </div>
  );
}
